import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import userApi from '../../Api/userApi';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
    container: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
        },
    },
    input: {
        minWidth: 250,
        [theme.breakpoints.down('xs')]: {
            minWidth: 'auto',
            width: '100%',
        },
    },
    button: {
        marginTop: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    linkedInfo: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
    },
    error: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

const AccountLinking = () => {
    const classes = useStyles();
    const intl = useIntl();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [linkedAccount, setLinkedAccount] = useState(null);
    const [digitaliadaEmail, setDigitaliadaEmail] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchLinkedAccount();
    }, []);

    const fetchLinkedAccount = async () => {
        setLoading(true);
        try {
            const response = await userApi.getLinkedAccount();
            if (response.data.linked) {
                setLinkedAccount(response.data.linkedAccount);
            } else {
                setLinkedAccount(null);
            }
        } catch (err) {
            console.error('Failed to fetch linked account', err);
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = (err) => {
        const errorCode = err.response?.data?.error;
        if (errorCode) {
            const translationKey = `accountLinking.error.${errorCode}`;
            const translated = intl.formatMessage({ id: translationKey, defaultMessage: '' });
            if (translated) {
                return translated;
            }
        }
        // Fallback to backend message or generic error
        return err.response?.data?.message || intl.formatMessage({ id: 'accountLinking.error.unexpected' });
    };

    const handleLink = async () => {
        setSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await userApi.linkDigitaliadaAccount(digitaliadaEmail);
            setLinkedAccount(response.data.linkedAccount);
            setDigitaliadaEmail('');
            setSuccess(intl.formatMessage({ id: 'accountLinking.success.linked' }));
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleUnlink = async () => {
        setSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            await userApi.unlinkDigitaliadaAccount();
            setLinkedAccount(null);
            setSuccess(intl.formatMessage({ id: 'accountLinking.success.unlinked' }));
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && digitaliadaEmail.trim() && !submitting) {
            handleLink();
        }
    };

    if (loading) {
        return (
            <Grid container justify="center" className={classes.container}>
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid container direction="column" className={classes.container}>
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                    <FormattedMessage id="accountLinking.title" />
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    <FormattedMessage id="accountLinking.description" />
                </Typography>
            </Grid>

            {error && (
                <Grid item xs={12}>
                    <Alert severity="error" className={classes.error}>
                        {error}
                    </Alert>
                </Grid>
            )}

            {success && (
                <Grid item xs={12}>
                    <Alert severity="success" className={classes.error}>
                        {success}
                    </Alert>
                </Grid>
            )}

            {linkedAccount ? (
                <Grid item xs={12}>
                    <div className={classes.linkedInfo}>
                        <Typography variant="body1">
                            <FormattedMessage
                                id="accountLinking.linked.info"
                                values={{ email: linkedAccount.digitaliadaEmail }}
                            />
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <FormattedMessage
                                id="accountLinking.linked.date"
                                values={{
                                    date: new Date(linkedAccount.linkedAt).toLocaleDateString(),
                                }}
                            />
                        </Typography>
                    </div>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleUnlink}
                        disabled={submitting}
                        className={classes.button}
                    >
                        {submitting ? (
                            <CircularProgress size={24} />
                        ) : (
                            <FormattedMessage id="accountLinking.button.unlink" />
                        )}
                    </Button>
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <Grid container alignItems="flex-end" spacing={2}>
                        <Grid item xs={12} sm="auto">
                            <TextField
                                label={intl.formatMessage({ id: 'accountLinking.input.label' })}
                                placeholder={intl.formatMessage({ id: 'accountLinking.input.placeholder' })}
                                value={digitaliadaEmail}
                                onChange={(e) => setDigitaliadaEmail(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className={classes.input}
                                disabled={submitting}
                                helperText={intl.formatMessage({ id: 'accountLinking.input.helper' })}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleLink}
                                disabled={!digitaliadaEmail.trim() || submitting}
                                className={classes.button}
                            >
                                {submitting ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    <FormattedMessage id="accountLinking.button.link" />
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default AccountLinking;
